import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Loader } from "@progress/kendo-react-indicators";
import ErrorBanner from "../../components/ErrorBanner";
import { filterBy } from "@progress/kendo-data-query";

import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { FormInput } from "../../components/FormInput";
import { FormMultiSelect } from "../../components/FormMultiSelect";

import {
  useMutatePostBrokerUser,
  useMutatePutBrokerUser,
} from "../../data/services/brokerUserService";
import { useQueryGetClientsMasterList } from "../../data/services/clientAdminService";

const AddBrokerUser = ({ onSubmit, onClose, item }) => {
  const emailRegex = new RegExp(/\S+@\S+\.\S+/);
  const isEdit = item && item.userId ? true : false;
  const isInternal =
    (item && item.isInternalContext) || (item && item.userType === "INTERNAL")
      ? true
      : false;

  const assignmentRoleOptions = [
    { value: "manual", label: "Manual" },
    { value: "primary", label: "Primary" },
    { value: "secondary", label: "Secondary" },
    { value: "ga", label: "General Agent" },
  ];

  const [submitted, setSubmitted] = React.useState(false);
  const [clients, setClients] = React.useState([]);
  const [clientsDataSource, setClientsDataSource] = React.useState([]);
  const [selectedClients, setSelectedClients] = React.useState([]);
  const [selectedClientRoles, setSelectedClientRoles] = React.useState({});
  const [sfManagedClients, setSfManagedClients] = React.useState({});
  const [initialValue, setInitialValue] = React.useState({});
  const [submitError, setSubmitError] = React.useState("");

  const { isLoading: availableClientsLoading, data: availableClients } =
    useQueryGetClientsMasterList();
  const useMutatePostBrokerUserHandler = useMutatePostBrokerUser();
  const useMutatePutBrokerUserHandler = useMutatePutBrokerUser();

  React.useEffect(() => {
    if (isInternal) {
      if (isEdit && item) {
        setInitialValue({
          firstName: item.firstName || "",
          lastName: item.lastName || "",
          userEmail: item.userEmail || "",
        });
      }
      return;
    }

    if (!availableClientsLoading && availableClients && availableClients.data) {
      let tempAvailableClients = availableClients.data.map((client) => {
        client.clientDispName = `${client.clientId} - ${client.clientName}`;
        return client;
      });

      if (tempAvailableClients.length > 0) {
        setClients(tempAvailableClients);
        setClientsDataSource(tempAvailableClients.slice());

        if (isEdit && item) {
          let filteredClients = tempAvailableClients.filter((client) =>
            item.clients?.find((t) => t.clientId === client.clientId),
          );
          const initialRoles =
            item.clients?.reduce((roles, client) => {
              roles[client.clientId] = client.assignmentRole || "manual";
              return roles;
            }, {}) || {};
          const sfManaged =
            item.clients?.reduce((map, client) => {
              map[client.clientId] = client.assignmentSource === "salesforce";
              return map;
            }, {}) || {};

          setSelectedClients(filteredClients);
          setSelectedClientRoles(initialRoles);
          setSfManagedClients(sfManaged);

          setInitialValue({
            firstName: item.firstName || "",
            lastName: item.lastName || "",
            userEmail: item.userEmail || "",
            clientIds: filteredClients,
          });
        } else {
          setSelectedClientRoles({});
        }
      } else {
        setClients([]);
        setClientsDataSource([]);
        setSelectedClientRoles({});
      }
    }
  }, [availableClientsLoading, availableClients, isEdit, item, isInternal]);

  const handleSubmit = async (data) => {
    let entity = {
      firstName: data.firstName,
      lastName: data.lastName,
      userEmail: data.userEmail,
      userType: isInternal ? "INTERNAL" : "BROKER",
      clients: isInternal ? [] : selectedClients.map((e) => e.clientId),
      clientAssignments: isInternal
        ? []
        : selectedClients.map((client) => ({
            clientId: client.clientId,
            assignmentRole: selectedClientRoles[client.clientId] || "manual",
          })),
    };

    setSubmitted(true);
    setSubmitError("");

    try {
      if (isEdit) {
        entity.userId = item.userId;
        await useMutatePutBrokerUserHandler.mutateAsync(entity);
      } else {
        await useMutatePostBrokerUserHandler.mutateAsync(entity);
      }
      await onSubmit();
    } catch (error) {
      setSubmitError(
        `Unable to save ${isInternal ? "internal" : "broker"} user. Please review the form and try again.`,
      );
      setSubmitted(false);
    }
  };

  const handleChange = (event) => {
    setSelectedClients(event.value);
    setSelectedClientRoles((currentRoles) => {
      const nextRoles = {};
      event.value.forEach((client) => {
        nextRoles[client.clientId] = currentRoles[client.clientId] || "manual";
      });
      return nextRoles;
    });
  };

  const handleRoleChange = (clientId, assignmentRole) => {
    setSelectedClientRoles((currentRoles) => ({
      ...currentRoles,
      [clientId]: assignmentRole,
    }));
  };

  const itemRender = (li, itemProps) => {
    const itemChildren = (
      <span>
        <input
          type="checkbox"
          name={itemProps.dataItem}
          checked={itemProps.selected}
          onChange={(e) => itemProps.onClick(itemProps.index, e)}
        />
        &nbsp;{li.props.children}
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const filterChange = (event) => {
    setClientsDataSource(filterBy(clients.slice(), event.filter));
  };

  const emailValidator = (value) =>
    emailRegex.test(value) ? "" : "Please enter a valid email.";
  const selectedClientsValidator = (value) =>
    isInternal || selectedClients.length > 0
      ? ""
      : "Please select at least one client.";

  const selected = selectedClients.length;

  const getDialogTitle = () => {
    if (isEdit) {
      return isInternal ? "Edit Internal User" : "Edit Broker User";
    }
    return isInternal ? "Add Internal User" : "Add Broker User";
  };

  const isReadyToRenderForm = !isEdit || initialValue.userEmail;

  return (
    <Dialog
      title={getDialogTitle()}
      onClose={() => {
        if (!submitted) onClose();
      }}
      width={720}
    >
      <ErrorBanner
        handler={useMutatePostBrokerUserHandler}
        onError={() => setSubmitted(false)}
      />
      <ErrorBanner
        handler={useMutatePutBrokerUserHandler}
        onError={() => setSubmitted(false)}
      />

      {isReadyToRenderForm &&
      (isInternal || (clients && clients.length > 0)) ? (
        <Form
          onSubmit={handleSubmit}
          initialValues={initialValue}
          ignoreModified={true}
          render={(formRenderProps) => (
            <FormElement horizontal={false} style={{ maxWidth: "100%" }}>
              <div
                style={{
                  maxHeight: "68vh",
                  overflowY: "auto",
                  paddingRight: 6,
                }}
              >
                {isInternal && (
                  <div
                    style={{
                      background: "#e3f2fd",
                      color: "#0d47a1",
                      padding: "10px 14px",
                      borderRadius: 4,
                      marginBottom: 16,
                      fontSize: 13,
                      border: "1px solid #bbdefb",
                    }}
                  >
                    ℹ️ <strong>Internal User Context:</strong> This user is
                    registered with global internal access (BDM / BAM). No
                    manual client assignments are needed.
                  </div>
                )}

                <Field
                  id={"firstName"}
                  name={"firstName"}
                  label={"First Name"}
                  component={FormInput}
                  required={true}
                  validator={(value) =>
                    value ? "" : "Please enter first name"
                  }
                />
                <Field
                  id={"lastName"}
                  name={"lastName"}
                  label={"Last Name"}
                  component={FormInput}
                  required={true}
                  validator={(value) => (value ? "" : "Please enter last name")}
                />
                <Field
                  id={"userEmail"}
                  name={"userEmail"}
                  label={"User Email"}
                  component={FormInput}
                  required={true}
                  disabled={isEdit}
                  validator={emailValidator}
                />

                {!isInternal && (
                  <>
                    <Field
                      id={"clientIds"}
                      name={"clientIds"}
                      label={"Assigned Clients"}
                      component={FormMultiSelect}
                      required={true}
                      validator={selectedClientsValidator}
                      data={clientsDataSource}
                      itemRender={itemRender}
                      textField={"clientDispName"}
                      dataItemKey={"clientId"}
                      onChange={handleChange}
                      autoClose={false}
                      value={selectedClients}
                      filterable={true}
                      onFilterChange={filterChange}
                      tags={
                        selected > 0
                          ? [
                              {
                                text: `${selected} clients selected`,
                                data: [...selectedClients],
                              },
                            ]
                          : []
                      }
                    />

                    {selectedClients.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            marginBottom: 8,
                          }}
                        >
                          Assignment Roles
                        </div>
                        <div
                          style={{
                            maxHeight: 260,
                            overflowY: "auto",
                            border: "1px solid #e2e2e2",
                            borderRadius: 4,
                            background: "#fafafa",
                            padding: 8,
                          }}
                        >
                          {selectedClients.map((client) => {
                            const isSfManaged =
                              !!sfManagedClients[client.clientId];
                            return (
                              <div
                                key={client.clientId}
                                style={{ marginBottom: 8 }}
                              >
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 160px",
                                    gap: 12,
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                    }}
                                  >
                                    {client.clientDispName}
                                    {isSfManaged && (
                                      <span
                                        style={{
                                          fontSize: 10,
                                          background: "#e8f5e9",
                                          color: "#2e7d32",
                                          border: "1px solid #a5d6a7",
                                          borderRadius: 3,
                                          padding: "1px 5px",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        Salesforce
                                      </span>
                                    )}
                                  </span>
                                  <select
                                    aria-label={`${client.clientId} Assignment Role`}
                                    value={
                                      selectedClientRoles[client.clientId] ||
                                      "manual"
                                    }
                                    onChange={(event) =>
                                      handleRoleChange(
                                        client.clientId,
                                        event.target.value,
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      height: 30,
                                      border: "1px solid #c5c5c5",
                                      borderRadius: 4,
                                      backgroundColor: "#fff",
                                      padding: "0 8px",
                                    }}
                                  >
                                    {assignmentRoleOptions.map((roleOption) => (
                                      <option
                                        key={roleOption.value}
                                        value={roleOption.value}
                                      >
                                        {roleOption.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                {isSfManaged && (
                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: "#e65100",
                                      marginTop: 2,
                                    }}
                                  >
                                    ⚠ Managed by Salesforce. Role changes are
                                    temporary and will be reset on the next
                                    sync.
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
                      Assignment roles selected here are saved to the portal. A
                      future Salesforce sync can still realign synced broker
                      roles.
                    </div>
                  </>
                )}

                {submitError && (
                  <div
                    style={{ marginTop: 10, color: "#c62828", fontSize: 12 }}
                  >
                    {submitError}
                  </div>
                )}
              </div>

              <DialogActionsBar layout={"end"}>
                {submitted && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      marginRight: 12,
                      color: "#555",
                      fontSize: 12,
                    }}
                  >
                    <Loader type="infinite-spinner" size="small" />
                    Saving...
                  </span>
                )}
                <Button onClick={onClose} disabled={submitted}>
                  {"Cancel"}
                </Button>
                <Button
                  type={"submit"}
                  themeColor={"primary"}
                  disabled={
                    submitted ||
                    (isEdit
                      ? (!isInternal && selectedClients.length === 0) ||
                        !formRenderProps.allowSubmit
                      : !formRenderProps.allowSubmit)
                  }
                >
                  {isEdit
                    ? "Update User"
                    : `Add ${isInternal ? "Internal User" : "Broker User"} & Send Invitation`}
                </Button>
              </DialogActionsBar>
            </FormElement>
          )}
        />
      ) : (
        <span>Loading... </span>
      )}
    </Dialog>
  );
};

export default AddBrokerUser;
